import api from "./api";

const adminService = {
  // ==========================================
  // Dashboard & Monitoring
  // ==========================================

  /**
   * Get main dashboard statistics
   */
  getDashboardStats: async () => {
    const response = await api.get("/admin/dashboard");
    return response.data;
  },

  /**
   * Get queue statistics for background jobs
   */
  getQueueStats: async () => {
    const response = await api.get("/admin/queue-stats");
    return response.data;
  },

  /**
   * Get bookings that require immediate attention
   */
  getUrgentBookings: async () => {
    const response = await api.get("/admin/urgent-bookings");
    return response.data;
  },

  /**
   * Get bookings stuck in matching process
   */
  getStuckBookings: async (threshold = 2) => {
    const response = await api.get(
      `/admin/matching/stuck?threshold=${threshold}`,
    );
    return response.data;
  },

  // ==========================================
  // Booking Management
  // ==========================================

  /**
   * Query all bookings with filters
   */
  getBookings: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/bookings?${query}`);
    return response.data;
  },

  /**
   * Get booking by ID
   */
  getBookingById: async (bookingId) => {
    const response = await api.get(`/admin/bookings/${bookingId}`);
    return response.data;
  },

  /**
   * Update a booking
   */
  updateBooking: async (bookingId, data) => {
    const response = await api.put(`/admin/bookings/${bookingId}`, data);
    return response.data;
  },

  /**
   * Assign a service provider to a booking
   */
  assignProvider: async (bookingId, serviceProviderId) => {
    const response = await api.put(
      `/admin/bookings/${bookingId}/assign-provider`,
      {
        serviceProviderId,
      },
    );
    return response.data;
  },

  /**
   * Retry matching for a booking
   */
  retryMatching: async (bookingId) => {
    const response = await api.post(
      `/admin/bookings/${bookingId}/retry-matching`,
    );
    return response.data;
  },

  /**
   * Get payment details for a booking
   */
  getBookingPaymentDetails: async (bookingId) => {
    const response = await api.get(
      `/admin/bookings/${bookingId}/payment-details`,
    );
    return response.data;
  },

  /**
   * Retry a failed payment
   */
  retryPayment: async (bookingId) => {
    const response = await api.post(
      `/admin/bookings/${bookingId}/retry-payment`,
    );
    return response.data;
  },

  /**
   * Retry a failed transfer
   */
  retryTransfer: async (bookingId) => {
    const response = await api.post(
      `/admin/bookings/${bookingId}/retry-transfer`,
    );
    return response.data;
  },

  // ==========================================
  // User Management
  // ==========================================

  /**
   * Query all users with filters
   */
  getUsers: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/users?${query}`);
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUserById: async (userId, view = "all") => {
    const response = await api.get(`/admin/users/${userId}?view=${view}`);
    return response.data;
  },

  /**
   * Create a new user (including admins)
   */
  createUser: async (data) => {
    const response = await api.post("/admin/users", data);
    return response.data;
  },

  /**
   * Update a user
   */
  updateUser: async (userId, data) => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  /**
   * Delete a user
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // ==========================================
  // Service Provider Management
  // ==========================================

  /**
   * Query all service providers with filters
   */
  getServiceProviders: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/service-providers?${query}`);
    return response.data;
  },

  /**
   * Get service provider statistics
   */
  getServiceProviderStats: async (userId) => {
    const response = await api.get(`/admin/service-providers/${userId}/stats`);
    return response.data;
  },

  /**
   * Update provider's categories
   */
  updateProviderCategories: async (
    userId,
    { addCategories, removeCategories },
  ) => {
    const response = await api.put(
      `/admin/service-providers/${userId}/categories`,
      {
        addCategories,
        removeCategories,
      },
    );
    return response.data;
  },

  // ==========================================
  // Category Approvals
  // ==========================================

  /**
   * Get pending category approvals
   */
  getPendingCategoryApprovals: async () => {
    const response = await api.get("/admin/category-approvals/pending");
    return response.data;
  },

  /**
   * Process a category approval (approve or reject)
   */
  processCategoryApproval: async (
    userId,
    categoryId,
    action,
    rejectionReason = null,
  ) => {
    const data = { action };
    if (rejectionReason) {
      data.rejectionReason = rejectionReason;
    }
    const response = await api.put(
      `/admin/category-approvals/${userId}/${categoryId}`,
      data,
    );
    return response.data;
  },

  /**
   * Get provider's category approvals
   */
  getProviderCategoryApprovals: async (userId) => {
    const response = await api.get(
      `/admin/service-providers/${userId}/category-approvals`,
    );
    return response.data;
  },

  // ==========================================
  // Category Management
  // ==========================================

  /**
   * Get all categories with optional stats
   */
  getCategories: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/categories?${query}`);
    return response.data;
  },

  /**
   * Create a new category
   */
  createCategory: async (data) => {
    const response = await api.post("/admin/categories", data);
    return response.data;
  },

  /**
   * Update a category
   */
  updateCategory: async (categoryId, data) => {
    const response = await api.put(`/admin/categories/${categoryId}`, data);
    return response.data;
  },

  /**
   * Delete a category
   */
  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/admin/categories/${categoryId}`);
    return response.data;
  },

  // ==========================================
  // Reviews Management
  // ==========================================

  /**
   * Get all reviews with filters
   */
  getReviews: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/reviews?${query}`);
    return response.data;
  },

  // ==========================================
  // Pricing Config Management
  // ==========================================

  /**
   * Get all pricing configs (optionally filtered by categoryId or isActive)
   */
  getPricingConfigs: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/pricing-configs?${query}`);
    return response.data;
  },

  /**
   * Get a single pricing config by ID
   */
  getPricingConfigById: async (configId) => {
    const response = await api.get(`/admin/pricing-configs/${configId}`);
    return response.data;
  },

  /**
   * Create a new pricing config
   */
  createPricingConfig: async (data) => {
    const response = await api.post("/admin/pricing-configs", data);
    return response.data;
  },

  /**
   * Update a pricing config
   */
  updatePricingConfig: async (configId, data) => {
    const response = await api.put(`/admin/pricing-configs/${configId}`, data);
    return response.data;
  },

  /**
   * Delete a pricing config
   */
  deletePricingConfig: async (configId) => {
    await api.delete(`/admin/pricing-configs/${configId}`);
    return configId;
  },
};

export default adminService;
